import { useCallback, useRef } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { MathUtils } from 'three';

import { roundToDecimals } from '@/modules/common/helpers/math';

type Props = {
  value: number;
  onChange: (value: number) => void;
  onBlur?: (value: number) => void;
  parseDraft: (raw: string) => number | null;
  draft: string;
  setDraft: Dispatch<SetStateAction<string>>;
  isEditing: boolean;
  setIsEditing: Dispatch<SetStateAction<boolean>>;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  disabled?: boolean;
};

function formatValue(value: number, decimals: number) {
  return String(roundToDecimals(value, decimals));
}

const STEPPER_BUTTON_SX = {
  width: 16,
  height: 16,
  minWidth: 16,
  padding: 0,
  borderRadius: 0,
  backgroundColor: 'white',
  color: 'text.primary',
  '&:active': {
    color: 'primary.main',
  },
  '&.Mui-focusVisible': {
    color: 'primary.main',
  },
  '& .MuiSvgIcon-root': {
    width: 12,
    height: 12,
    fontSize: 10,
  },
};

export default function NumberInputButtons(props: Props) {
  const {
    value,
    onChange,
    onBlur,
    parseDraft,
    draft,
    setDraft,
    isEditing,
    setIsEditing,
    min,
    max,
    step = 1,
    decimals = 2,
    disabled = false,
  } = props;

  const valueRef = useRef(value);
  const timerRef = useRef<NodeJS.Timer | null>(null);

  const handleCommit = useCallback(
    () => {
      const base = parseDraft(draft);
      const current = base ?? value;
      const nextValue = roundToDecimals(
        MathUtils.clamp(current, min ?? current, max ?? current),
        decimals,
      );

      onChange(nextValue);
      onBlur?.(nextValue);
      setDraft(formatValue(nextValue, decimals));
      setIsEditing(false);
    },
    [decimals, draft, max, min, onBlur, onChange, parseDraft, setDraft, setIsEditing, value],
  );
      
  const handleChange = useCallback(
    (step: number) => {
      const nextValue = valueRef.current + step;
      valueRef.current = nextValue;
      setDraft(formatValue(nextValue, decimals));
    },
    [decimals, setDraft],
  );

  const increment = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();

      valueRef.current = value;
      setIsEditing(true);
      handleChange(step);
      timerRef.current = setInterval(() => {
        handleChange(step);
      }, 100);
    },
    [handleChange, setIsEditing, step, value],
  );

  const decrement = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();

      valueRef.current = value;
      setIsEditing(true);
      handleChange(-step);
      timerRef.current = setInterval(() => {
        handleChange(-step);
      }, 100);
    },
    [handleChange, setIsEditing, step, value],
  );

  const handleMouseUp = useCallback(
    () => {
      setIsEditing(false);
      handleCommit();

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    },
    [handleCommit, setIsEditing],
  );

  const numericDraft = parseDraft(draft);
  const effectiveValue = isEditing ? numericDraft ?? value : value;

  const isAtMin = typeof min === 'number' && effectiveValue <= min;
  const isAtMax = typeof max === 'number' && effectiveValue >= max;

  return (
    <Stack
      spacing={0}
      sx={(theme) => ({
        flex: '0 0 20%',
        minWidth: 32,
        maxWidth: 48,
        border: `1px solid ${theme.palette.neutral.grey}`,
        borderRadius: 1,
        backgroundColor: 'white',
      })}
      overflow='hidden'
      alignItems='stretch'
      justifyContent='space-between'
    >
      <IconButton
        disabled={disabled || isAtMax}
        tabIndex={-1}
        aria-label='increase value'
        sx={{ ...STEPPER_BUTTON_SX, width: '100%' }}
        onMouseDown={increment}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp} // Stops action if cursor leaves the button
      >
        <AddIcon />
      </IconButton>
      <IconButton
        disabled={disabled || isAtMin}
        tabIndex={-1}
        aria-label='decrease value'
        sx={{ ...STEPPER_BUTTON_SX, width: '100%' }}
        onMouseDown={decrement}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp} // Stops action if cursor leaves the button
      >
        <RemoveIcon />
      </IconButton>
    </Stack>
  );
}
